// Shared data + palettes for the home-page concept lab.
// Nothing here touches the live site — the lab lives on /lab only.

import { allProjects } from '../content-and-configs/index';

export const LIVE_URLS = {
  tonelab: 'https://tonelab.so',
  moodify: 'https://moodify.so/mood-boards',
  foundry: 'https://thefoundrybrief.com',
  chibidash: 'https://chibidash.com',
  findbrandvoice: 'https://findbrandvoice.com',
  onepiecequiz: 'https://www.onepiecetrivia.com',
  'prove-it': 'https://proveit.so',
  summit: 'https://summitproduction.co',
  'tam-aljanub': 'https://aljanubrestaurant.com',
  'logo-builder': 'https://okaylogo.com',
  pudeuc: 'https://pudeuc.com',
  riyalbusiness: 'https://www.riyalbusiness.com',
  'the-sportship': 'https://www.thesportsship.com',
  stash: 'https://www.instash.app'
};

export const COVERS = {
  pudeuc: '/pudeuc-brand/pudeuc-logo-navy-background.webp',
  riyalbusiness: '/riyalbusiness/cover.webp',
  summit: '/summit/cover.webp',
  'the-sportship': '/sportship-site/cover.webp',
  najdar: '/najdar-brand/najdar3.webp',
  pawtimo: '/pawtimo-icon.png',
  stash: '/portfolio-screens/stash/desktop-2.png',
  findbrandvoice: '/portfolio-screens/findbrandvoice/cover.png',
  'prove-it': '/portfolio-screens/proveit/cover.png',
  foundry: '/portfolio-screens/foundry/cover.png',
  chibidash: '/portfolio-screens/chibidash/cover.png',
  onepiecequiz: '/portfolio-screens/onepiecequiz/cover.png',
  'tam-aljanub': '/portfolio-screens/tam-aljanub/cover.png',
  moodify: '/portfolio-screens/moodify/cover.png',
  tonelab: '/portfolio-screens/tonelab/cover.png',
  'logo-builder': '/portfolio-screens/okaylogo/cover.png'
};

/* Each project as its brand: colour, logo (or its name set in its own type).
   The home deck shows these instead of screenshots; /?covers=screens shows the screenshots. */
export const BRANDS = {
  // logo + its name or line underneath, unless the logo already carries the name
  tonelab:          { bgImage: '/portfolio-screens/tonelab/cover.png', blur: 4, overlay: 'rgba(20,19,21,0.40)', bg: '#141315', logo: '/brand-logos/sm/tonelab.png', size: '24%', radius: '22%', name: 'Tonelab', color: '#F8F9F9' },
  findbrandvoice: { bgImage: '/portfolio-screens/findbrandvoice/cover.png', blur: 4, overlay: 'rgba(255,255,255,0.28)', bg: 'linear-gradient(180deg, #A7CBEB 0%, #DCEBF8 55%, #FFFFFF 100%)', word: 'FindBrandVoice', color: '#0F172A', weight: 800, spacing: '-0.04em', line: "Discover and define your brand's voice" },
  'prove-it': { bg: '#080808', bgImage: '/portfolio-screens/proveit/cover.png', blur: 4, overlay: 'rgba(8,8,8,0.32)', word: 'Prove It', color: '#FFFFFF', weight: 800, spacing: '-0.04em' },
  'logo-builder':   { bgImage: '/portfolio-screens/okaylogo/cover.png', blur: 4, overlay: 'rgba(21,20,22,0.40)', bg: '#151416', logo: '/brand-logos/sm/okaylogo-icon.png', size: '24%', radius: '22%', name: 'OkayLogo', color: '#FFFFFF' },
  stash:            { bgImage: '/portfolio-screens/stash/desktop-2.png', blur: 4, overlay: 'rgba(225,29,72,0.38)', bg: 'linear-gradient(135deg, #E11D48 0%, #F87171 100%)', logo: '/brand-logos/sm/stash-logo.png', size: '22%', radius: '22%', name: 'Stash', line: 'Save anything. Find everything.', color: '#FFFFFF' },
  moodify: { bgImage: '/portfolio-screens/moodify/cover.png', blur: 4, overlay: 'rgba(5,8,7,0.40)', bg: 'radial-gradient(120% 90% at 30% 0%, #0E3B2E 0%, #050807 70%)', logo: '/brand-logos/sm/moodify-cat-white.png', size: '18%', name: 'Moodify', line: 'Brainstorm visually, move fast', color: '#FFFFFF' },
  // the TFB square takes the card's own navy, so mark and card are one surface
  foundry: { bg: '#06223D', logo: '/brand-logos/sm/tfb-letters.png', size: '30%', name: 'The Foundry Brief', color: '#B49E89', font: "'Financier Display', Georgia, serif", nameWeight: 400, tight: true },
  pudeuc: { bg: '#1E3150', image: '/pudeuc-brand/social-share.webp' },
  riyalbusiness: { bg: '#2B1A12', logo: '/riyalbusiness/logo-on-brown.svg', size: '58%' },
  summit: { bg: '#FAFAF9', bgImage: '/summit/girllooking.webp', blur: 4, overlay: 'rgba(250,250,249,0.40)', logo: '/brand-logos/sm/wordmark-trim.png', size: '62%', line: 'Saudi production event management', color: '#1f1f1f', lineCaps: true, tight: true },
  'the-sportship': { bg: '#FFFFFF', bgImage: '/sportship-site/cover.webp', blur: 4, overlay: 'rgba(255,255,255,0.40)', logo: '/brand-logos/sm/tss-black.png', size: '30%', name: 'The Sportship', color: '#0B0B0C', nameStyle: 'italic', nameWeight: 900 },
  'tam-aljanub': { bg: '#16293D', logo: '/brand-logos/sm/tam-aljanub-navy.png', size: '50%' },
  chibidash:        { bgImage: '/portfolio-screens/chibidash/cover.png', blur: 4, overlay: 'rgba(31,28,26,0.38)', bg: '#1F1C1A', logo: '/brand-logos/sm/chibidash-icon.png', size: '26%', radius: '50%', name: 'Chibi Dash', line: 'A calm space for anime minds', color: '#F2EDE6' },
  onepiecequiz: { bg: '#1E3352', bgImage: '/brand-logos/onepiece-bg.webp', blur: 4, overlay: 'rgba(255,255,255,0.08)', logo: '/brand-logos/sm/onepiecequiz-icon.png', size: '30%', radius: '50%', shadow: true },
  // the icon's own pink, no rounded square: icon and card read as one layer
  pawtimo: { bg: '#CF5A70', bgImage: '/brand-logos/pawtimo-bg.webp', blur: 4, bgPos: 'center 62%', overlay: 'rgba(255,255,255,0.10)', logo: '/brand-logos/sm/pawtimo-icon.png', size: '22%', radius: '28%', shadow: true, line: 'Happy pets, calm family', color: '#FFFFFF' },
  najdar: { bgImage: '/najdar-brand/najdar3.webp', blur: 4, overlay: 'rgba(244,240,233,0.50)', bg: 'linear-gradient(160deg, #F4F0E9 0%, #DED6C9 100%)', logo: '/brand-logos/sm/najdar.png', size: '62%', blend: 'multiply' }
};

// hidden projects keep their page (shareable by link) but stay off the home deck and the menu
export const PROJECTS = allProjects.filter((p) => !p.hidden).map((p, i) => ({
  ...p,
  n: String(i + 1).padStart(2, '0'),
  cover: COVERS[p.id] || null,
  live: LIVE_URLS[p.id] || null,
  href: `/projects/${p.id}`
}));

export const INTRO = {
  name: 'Fahad Nimer',
  handle: 'fahadnimer.com',
  role: 'Designer & builder',
  line: "I build things. This is where I keep what I've been working on.",
  long:
    'Brand systems, web apps, design tools, and the odd interactive experiment. ' +
    'Most of what is here shipped; some of it is still moving.',
  email: 'fahadnimer1@gmail.com'
};

/* Each concept ships its own palette so light and dark can render side by side
   without fighting the site-wide CSS variables. */
export const palettes = {
  editorial: {
    light: { bg: '#f4f3ef', panel: '#ffffff', ink: '#111110', dim: '#6b6a64', line: '#dcdad2', accent: '#1b4dff' },
    dark:  { bg: '#0d0d0c', panel: '#151513', ink: '#f2f1ea', dim: '#8d8b82', line: '#2a2926', accent: '#7f9bff' }
  },
  kinetic: {
    light: { bg: '#eeff41', panel: '#ffffff', ink: '#0a0a0a', dim: '#3d3d18', line: '#0a0a0a', accent: '#ff3b1f' },
    dark:  { bg: '#0a0a0a', panel: '#141414', ink: '#eeff41', dim: '#9a9a7a', line: '#eeff41', accent: '#ff3b1f' }
  },
  cinema: {
    light: { bg: '#e8e6e1', panel: '#ffffff', ink: '#14130f', dim: '#6f6c63', line: '#cfccc4', accent: '#b4552d' },
    dark:  { bg: '#070707', panel: '#101010', ink: '#f6f4ef', dim: '#8a877f', line: '#232220', accent: '#e0764a' }
  },
  terminal: {
    light: { bg: '#f6f7f4', panel: '#ffffff', ink: '#0f1a12', dim: '#5c6b5f', line: '#d5ddd4', accent: '#0a7d3c' },
    dark:  { bg: '#05070a', panel: '#0b0f14', ink: '#c8f7d4', dim: '#5f7e69', line: '#16211b', accent: '#3ef58a' }
  },
  journal: {
    light: { bg: '#fbf9f4', panel: '#ffffff', ink: '#22201b', dim: '#7a756a', line: '#e6e1d5', accent: '#8a5a2b' },
    dark:  { bg: '#12110f', panel: '#1a1815', ink: '#efe9dd', dim: '#948d80', line: '#2b2823', accent: '#d3a06a' }
  },
  living: {
    light: { bg: '#f7f7fb', panel: 'rgba(255,255,255,0.72)', ink: '#101018', dim: '#5f6070', line: 'rgba(0,0,0,0.09)', accent: '#4f46e5' },
    dark:  { bg: '#05050a', panel: 'rgba(255,255,255,0.05)', ink: '#f1f1f8', dim: '#9698ad', line: 'rgba(255,255,255,0.1)', accent: '#8b7bff' }
  },
  paper: {
    light: { bg: '#f5f2e8', panel: '#fffdf6', ink: '#1e1c17', dim: '#6f6a5c', line: '#ddd6c4', accent: '#c2452d' },
    dark:  { bg: '#16150f', panel: '#1e1c15', ink: '#f0ebdc', dim: '#8f897a', line: '#302c22', accent: '#e07a5f' }
  },
  timeline: {
    light: { bg: '#ffffff', panel: '#f7f7f8', ink: '#0e0e10', dim: '#71717a', line: '#e4e4e7', accent: '#0f766e' },
    dark:  { bg: '#09090b', panel: '#131316', ink: '#fafafa', dim: '#8b8b93', line: '#232327', accent: '#2dd4bf' }
  },
  manifesto: {
    light: { bg: '#ffffff', panel: '#fafafa', ink: '#0a0a0a', dim: '#737373', line: '#e5e5e5', accent: '#dc2626' },
    dark:  { bg: '#0a0a0a', panel: '#141414', ink: '#fafafa', dim: '#8a8a8a', line: '#242424', accent: '#f87171' }
  },
  gallery: {
    light: { bg: '#ffffff', panel: '#f4f4f5', ink: '#0a0a0a', dim: '#71717a', line: '#e7e7ea', accent: '#111111' },
    dark:  { bg: '#050505', panel: '#121212', ink: '#fafafa', dim: '#8f8f95', line: '#1f1f22', accent: '#ffffff' }
  },
  deck: {
    light: { bg: '#f6f5f2', panel: '#ffffff', ink: '#111111', dim: '#6b6b6b', line: '#e6e3dd', accent: '#1668dc' },
    dark:  { bg: '#0a0a0b', panel: '#141416', ink: '#f2f2f2', dim: '#8e8e93', line: '#232327', accent: '#4c9aff' }
  },
  split: {
    light: { bg: '#fafaf9', panel: '#ffffff', ink: '#1c1917', dim: '#78716c', line: '#e7e5e4', accent: '#9a3412' },
    dark:  { bg: '#0c0a09', panel: '#171412', ink: '#fafaf9', dim: '#8b8580', line: '#26221f', accent: '#fb923c' }
  },
  magazine: {
    light: { bg: '#fffdf8', panel: '#f6f2e8', ink: '#171310', dim: '#6d665c', line: '#e2dbcb', accent: '#7c2d12' },
    dark:  { bg: '#0f0d0b', panel: '#191512', ink: '#f7f2e8', dim: '#918a7d', line: '#282320', accent: '#e8a33d' }
  },
  showreel: {
    light: { bg: '#101014', panel: '#1a1a20', ink: '#f5f5f7', dim: '#9a9aa5', line: '#26262e', accent: '#f43f5e' },
    dark:  { bg: '#000000', panel: '#0e0e12', ink: '#f5f5f7', dim: '#8a8a95', line: '#1c1c22', accent: '#fb7185' }
  },
  bento: {
    light: { bg: '#f0f1f5', panel: '#ffffff', ink: '#15161c', dim: '#6b6d7a', line: '#e2e3ea', accent: '#5b5bd6' },
    dark:  { bg: '#0b0b0f', panel: '#16161d', ink: '#f3f3f7', dim: '#8b8c99', line: '#25262f', accent: '#8b8bf5' }
  }
};

/* Fahad's own words, lifted from the current profile page. The journal-leaning
   concepts are built around this voice rather than marketing copy. */
export const JOURNAL = [
  "I've spent the past few years trying to build.",
  "I've gone through phases, overthinking, experimenting, starting again. Sometimes things moved forward. Sometimes I left them for later.",
  "This site is where I keep what I've been working on lately. Not everything is finished. Not everything is clear.",
  "But it's all part of the process."
];

export const ENTRIES = [
  { year: '2026', title: 'Still shipping', note: 'The Foundry Brief found its shape. The Sportship got a website. Kept going.' },
  { year: '2025', title: 'The busy year',  note: 'Tonelab, Prove It, OkayLogo, Stash, Find Brand Voice. Five things out the door.' },
  { year: '2024', title: 'Learning to finish', note: 'Moodify and the first brand systems. Started caring about the ending, not the idea.' },
  { year: '2023', title: 'Phases', note: 'Overthinking, experimenting, starting again. A lot of half-built things.' }
];

export const ease = [0.22, 1, 0.36, 1];

// Every concept gets the same scroll-in behaviour so comparisons stay fair.
export const rise = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease, delay: 0.04 * i }
  })
};
